import { useState } from 'react'
import { WorkerEvaluationShift } from 'api/review/getWorkReview'
import { TryoutLabel } from 'components/tryouts/TryoutLabel'
import { formatInTimeZone } from 'date-fns-tz'
import { getLocalTimezone } from 'lib/time'
import {
  ChevronDown,
  ChevronRight,
  LucideCalendarClock,
  LucideToggleLeft,
} from 'lucide-react'
import { ShiftDetailProvider } from 'pages/ShiftDetailPage/ShiftDetailProvider'
import { useAuthContext } from 'components/auth'
import { Flex, Box, IconButton, Link, Paragraph, Text } from 'ui'
import { WorkerEvaluationShiftWorkers } from './WorkerEvaluationShiftWorkers'

type Props = {
  shift: WorkerEvaluationShift
}

export const WorkerEvaluationShiftCard = ({ shift }: Props) => {
  const [isExpanded, setIsExpanded] = useState(true)
  const { company } = useAuthContext()
  const timezone = shift.location.timezone || getLocalTimezone()
  const startDate = new Date(shift.startsAt)
  const endDate = new Date(shift.endsAt)
  const roster = shift.position.roster

  return (
    <Box mb={3}>
      <Flex alignItems="flex-start" mb={2}>
        <Flex
          width="5.75rem"
          flexShrink={0}
          alignItems="center"
          justifyContent="center"
        >
          <IconButton
            aria-expanded={isExpanded}
            aria-label={isExpanded ? 'Hide workers' : 'Show workers'}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded ? (
              <ChevronDown size={20} />
            ) : (
              <ChevronRight size={20} />
            )}
          </IconButton>
        </Flex>
        <Box ml={2} minWidth={0} flex={1}>
          <Flex
            flexDirection={['column', 'column', 'row']}
            alignItems={['flex-start', 'flex-start', 'center']}
            flexWrap="wrap"
          >
            <Box mr={4} mb={[1, 1, 0]}>
              <Flex alignItems="center" mb={0}>
                <Paragraph my={0} fontWeight="bold" mr={2}>
                  {shift.position.name}
                </Paragraph>
                {shift.isTryout ? <TryoutLabel size="small" /> : null}
              </Flex>
              <Text fontSize={2}>{shift.location.name}</Text>
            </Box>
            <Flex flexDirection="row" alignItems="center" mr={4} mb={[1, 1, 0]}>
              <LucideCalendarClock size={16} />
              <Paragraph my={0} ml={2}>
                {formatInTimeZone(startDate, timezone, 'h:mma')} -{' '}
                {formatInTimeZone(endDate, timezone, 'h:mma zzz')}{' '}
                {shift.lunchLength && shift.lunchLength > 0
                  ? `• Unpaid Break: ${shift.lunchLength}mins`
                  : ''}
              </Paragraph>
            </Flex>
            {shift.isTryout && roster ? (
              <Flex flexDirection="row" alignItems="center" mb={[1, 1, 0]}>
                <LucideToggleLeft size={16} />
                <Paragraph my={0} ml={2}>
                  Qualifying workers for the{' '}
                  <Link to={`/workers/list/${roster.id}`}>{roster.name}</Link>
                </Paragraph>
              </Flex>
            ) : null}
          </Flex>
          {shift.isTryout ? (
            <Paragraph mt={2} mb={0} fontSize={2}>
              Workers from your Tryout will remain in the List for that position
              by default. Remove a worker from your List if you no longer wish
              to work with them. Removing them from your Tryout will not prevent
              them from seeing other shifts from your company.
            </Paragraph>
          ) : null}
        </Box>
      </Flex>

      {isExpanded && company ? (
        <ShiftDetailProvider companyId={company.id} shiftId={shift.id}>
          <WorkerEvaluationShiftWorkers />
        </ShiftDetailProvider>
      ) : null}
    </Box>
  )
}
